import React, { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Col, Row, Button, Divider, Select, Radio, DatePicker, RadioChangeEvent, message, Modal } from 'antd';
import { useLazyQuery, useMutation } from '@apollo/client';
import ParcelFilterStyle from "../../../../../delivery/components/ParcelFilterStyle";
import PricingItemPanel from '../../PriceItemPanel';
import DspDeliveryFeeViewTab from './DspDeliveryFeeViewTab';
import dayjs from 'dayjs';
import { GET_PRICING_SCHEME_DETAIL, PricingSchemeDetailResponse } from '../../graphql/queries/getPricingSchemeDetail';
import {
    GET_CLIENT_PRICING_SCHEME_APPLY_DETAIL,
    ClientPricingApplyDetailResponse
} from '../graphql/queries/getClientPricingSchemeApplyDetail';
import {
    BIND_PRICING_SCHEME,
    UNBIND_PRICING_SCHEME,
    APPEND_BIND_PRICING_SCHEME,
    BindPricingSchemeInput,
    UnbindPricingSchemeInput,
    AppendBindPricingSchemeInput,
    BindPricingSchemeResponse,
    UnbindPricingSchemeResponse,
    AppendBindPricingSchemeResponse
} from '../graphql/mutations/pricingSchemeApplyMutation';
import type { SelectOptionType } from 'graphql/types/SelectOptionType';
import { LIST_DSP_PRICING_SCHEME_SELECT_OPTIONS } from './graphql/queries/listDspPricingSchemeSelectOptions';
import { LIST_ALL_DSP_OPTIONS } from './graphql/queries/listAllDspOptions';
import {ResourceCode} from "../../../../../../constants/resourceCodes";
import {HasResource} from "../../../../../../components/HasResource";
import { resolvePricingSchemeMessage, translateGraphQLError } from "../../../../../../utils/graphqlUtils";
import { formatApplyTimeWithTimezone } from "../../../../../../utils/timezoneFormatter";

const DSP_CLIENT_ID = 0;

type DSPApplyMode = 'bind' | 'unbind' | 'bindEdit';

const DSPApplySchemeComp: React.FC<{
    businessScenario: number | null;
    mode: DSPApplyMode;
    onClose?: () => void;
    onSuccess?: () => void;
}> = ({ businessScenario, mode, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const isBindView = mode === 'bind';
    const isUnbindView = mode === 'unbind';
    const [schemeDesc, setSchemeDesc] = useState<string>('');
    const [schemeOptions, setSchemeOptions] = useState<SelectOptionType[]>([]);
    const [dspOptions, setDspOptions] = useState<SelectOptionType[]>([]);
    const [selectedSchemeId, setSelectedSchemeId] = useState<number | null>(null);
    const [feeItemList, setFeeItemList] = useState<Array<{ label: string; key: string; children: JSX.Element }>>([]);
    const [selectedDspIds, setSelectedDspIds] = useState<string[]>([]);
    const [unbindDspIds, setUnbindDspIds] = useState<string[]>([]);
    const [appendDspIds, setAppendDspIds] = useState<string[]>([]);
    const [effectiveDate, setEffectiveDate] = useState<dayjs.Dayjs | null>(null);
    const [timeZone, setTimeZone] = useState<string>('Asia/Shanghai');

    const [loadSchemeOptions, { data: schemeData, loading: schemeLoading }] = useLazyQuery<{ allOptions: SelectOptionType[] }>(
        LIST_DSP_PRICING_SCHEME_SELECT_OPTIONS
    );

    const [loadSchemeDetail, { data: schemeDetailData }] = useLazyQuery<PricingSchemeDetailResponse>(
        GET_PRICING_SCHEME_DETAIL,
        {
            fetchPolicy: 'network-only',
        }
    );

    const [loadDspOptions, { data: dspOptionsData, loading: dspOptionsLoading }] = useLazyQuery<{ allOptions: SelectOptionType[] }>(
        LIST_ALL_DSP_OPTIONS
    );

    const [loadApplyDetail, { data: applyDetailData, loading: applyDetailLoading }] = useLazyQuery<ClientPricingApplyDetailResponse>(
        GET_CLIENT_PRICING_SCHEME_APPLY_DETAIL,
        {
            fetchPolicy: 'network-only',
        }
    );

    const [bindPricingScheme, { loading: bindLoading }] = useMutation<BindPricingSchemeResponse>(BIND_PRICING_SCHEME);
    const [unbindPricingScheme, { loading: unbindLoading }] = useMutation<UnbindPricingSchemeResponse>(UNBIND_PRICING_SCHEME);
    const [appendBindPricingScheme, { loading: appendBindLoading }] = useMutation<AppendBindPricingSchemeResponse>(APPEND_BIND_PRICING_SCHEME);

    const feeItemTabMap: Record<string, { label: string; componentType: string }> = {
        '1001002001': { label: '配送费-快速', componentType: 'delivery' },
    };

    const handleSchemeChange = (value: string) => {
        if (value) {
            const schemeId = parseInt(value, 10);
            setSelectedSchemeId(schemeId);
            setSelectedDspIds([]);
            setUnbindDspIds([]);
            setAppendDspIds([]);
            setEffectiveDate(null);
            setTimeZone('Asia/Shanghai');
        } else {
            setSelectedSchemeId(null);
            setSchemeDesc('');
            setFeeItemList([]);
            setSelectedDspIds([]);
            setUnbindDspIds([]);
            setAppendDspIds([]);
            setEffectiveDate(null);
            setTimeZone('Asia/Shanghai');
        }
    };

    const handleSelectedDspChange = (value: string[]) => {
        setSelectedDspIds(value);
    };

    const handleUnbindDspChange = (value: string[]) => {
        setUnbindDspIds(value);
    };

    const handleAppendDspChange = (value: string[]) => {
        setAppendDspIds(value);
    };

    const resetAllStates = () => {
        setSchemeDesc('');
        setSchemeOptions([]);
        setDspOptions([]);
        setSelectedSchemeId(null);
        setFeeItemList([]);
        setSelectedDspIds([]);
        setUnbindDspIds([]);
        setAppendDspIds([]);
        setEffectiveDate(null);
        setTimeZone('Asia/Shanghai');
    };

    const handleClose = () => {
        resetAllStates();
        onClose?.();
    };

    const handleDateChange = (date: dayjs.Dayjs | null) => {
        setEffectiveDate(date);
    };

    const handleTimeZoneChange = (value: string) => {
        setTimeZone(value);
    };

    const validateForm = (): boolean => {
        if (!selectedSchemeId) {
            message.error('请选择方案');
            return false;
        }
        if (selectedDspIds.length === 0) {
            message.error('请选择DSP客户维度');
            return false;
        }
        if (!timeZone) {
            message.error('请选择时区');
            return false;
        }
        if (checkEffectType === 2 && !effectiveDate) {
            message.error('请选择生效日期');
            return false;
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) {
            return;
        }

        const input: BindPricingSchemeInput = {
            clientId: DSP_CLIENT_ID,
            pricingSchemeId: selectedSchemeId!,
            bindValue: selectedDspIds,
            effectiveNow: checkEffectType === 1,
            effectiveDate: checkEffectType === 2 ? effectiveDate?.format('YYYY-MM-DD') : null,
            timezone: timeZone
        };

        try {
            const { data } = await bindPricingScheme({
                variables: { input }
            });

            if (data?.bindPricingSchema?.success) {
                message.success(t('settlement.priceCenter.messages.bindSchemeSuccess'));
                setTimeout(() => {
                    onSuccess?.();
                }, 1000);

                setTimeout(() => {
                    resetAllStates();
                    onClose?.();
                }, 700);
            } else {
                message.error(resolvePricingSchemeMessage(data?.bindPricingSchema, t('settlement.priceCenter.messages.bindSchemeFailed')));
            }
        } catch (error: any) {
            message.error(translateGraphQLError(error, 'errors.INTERNAL_ERROR'));
        }
    };

    const getUnbindDspLabels = (ids: string[]) => {
        if (!ids || ids.length === 0) {
            return [];
        }
        const optionMap = new Map(unbindOptions.map(option => [option.value, option.label]));
        return ids.map(id => optionMap.get(id) || id);
    };

    const handleUnbindSave = () => {
        if (!selectedSchemeId) {
            message.error('请选择方案');
            return;
        }
        if (unbindDspIds.length === 0) {
            message.error('请选择要解绑的DSP客户维度');
            return;
        }

        const unbindDspLabels = getUnbindDspLabels(unbindDspIds);

        Modal.confirm({
            title: '确认解绑方案',
            icon: <span style={{ color: '#ff4d4f', fontSize: '22px' }}>⚠️</span>,
            content: (
                <div>
                    <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#ff4d4f' }}>
                        警告：此操作不可撤销！
                    </p>
                    <p style={{ marginBottom: '8px' }}>您即将解绑以下信息：</p>
                    <div style={{
                        padding: '12px',
                        background: '#fff7e6',
                        border: '1px solid #ffd591',
                        borderRadius: '4px',
                        marginTop: '12px'
                    }}>
                        <p><strong>方案ID：</strong>{selectedSchemeId}</p>
                        <p><strong>解绑维度：</strong>{unbindDspLabels.join(', ')}</p>
                    </div>
                    <p style={{ marginTop: '16px', color: '#ff4d4f' }}>
                        解绑后，该方案将不再对这些维度生效。
                    </p>
                </div>
            ),
            okText: '确认解绑',
            okType: 'danger',
            cancelText: '取消',
            width: 520,
            onOk: async () => {
                const input: UnbindPricingSchemeInput = {
                    clientId: DSP_CLIENT_ID,
                    pricingSchemeId: selectedSchemeId,
                    unBindValue: unbindDspIds,
                };

                try {
                    const { data } = await unbindPricingScheme({
                        variables: { input }
                    });

                    if (data?.unbindPricingSchema?.success) {
                        message.success(t('settlement.priceCenter.messages.unbindSchemeSuccess'));
                        onSuccess?.();
                        setTimeout(() => {
                            resetAllStates();
                            onClose?.();
                        }, 500);
                    } else {
                        message.error(resolvePricingSchemeMessage(data?.unbindPricingSchema, t('settlement.priceCenter.messages.unbindSchemeFailed')));
                    }
                } catch (error: any) {
                    message.error(translateGraphQLError(error, 'errors.INTERNAL_ERROR'));
                    throw error;
                }
            },
        });
    };

    const getDspLabelsByValues = (values: string[], options: SelectOptionType[]) => {
        if (!values || values.length === 0) {
            return [];
        }
        const optionMap = new Map(options.map(option => [option.value, option.label]));
        return values.map(id => optionMap.get(id) || id);
    };

    const handleAppendSave = () => {
        if (!selectedSchemeId) {
            message.error('请选择方案');
            return;
        }
        if (appendDspIds.length === 0) {
            message.error('请选择要追加绑定的DSP客户维度');
            return;
        }

        const appendDspLabels = getDspLabelsByValues(appendDspIds, dspOptions);

        Modal.confirm({
            title: '确认追加绑定方案',
            icon: <span style={{ color: '#ff4d4f', fontSize: '22px' }}>⚠️</span>,
            content: (
                <div>
                    <p style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '16px', color: '#ff4d4f' }}>
                        警告：此操作不可撤销！
                    </p>
                    <p style={{ marginBottom: '8px' }}>您即将为以下信息追加绑定：</p>
                    <div style={{
                        padding: '12px',
                        background: '#fff7e6',
                        border: '1px solid #ffd591',
                        borderRadius: '4px',
                        marginTop: '12px'
                    }}>
                        <p><strong>方案ID：</strong>{selectedSchemeId}</p>
                        <p><strong>追加绑定维度：</strong>{appendDspLabels.join(', ')}</p>
                    </div>
                    <p style={{ marginTop: '16px', color: '#ff4d4f' }}>
                        追加绑定后，该方案将对这些新增维度生效。
                    </p>
                    <p style={{ marginTop: '8px', color: '#666' }}>
                        生效时间/时区将沿用原绑定方案（不可编辑）。
                    </p>
                </div>
            ),
            okText: '确认追加绑定',
            okType: 'danger',
            cancelText: '取消',
            width: 520,
            onOk: async () => {
                const input: AppendBindPricingSchemeInput = {
                    clientId: DSP_CLIENT_ID,
                    pricingSchemeId: selectedSchemeId,
                    bindValue: appendDspIds,
                };

                try {
                    const { data } = await appendBindPricingScheme({
                        variables: { input }
                    });

                    if (data?.appendBindPricingSchema?.success) {
                        message.success(t('settlement.priceCenter.messages.appendBindSuccess'));
                        onSuccess?.();
                        setTimeout(() => {
                            resetAllStates();
                            onClose?.();
                        }, 700);
                    } else {
                        message.error(resolvePricingSchemeMessage(data?.appendBindPricingSchema, t('settlement.priceCenter.messages.appendBindFailed')));
                    }
                } catch (error: any) {
                    message.error(translateGraphQLError(error, 'errors.INTERNAL_ERROR'));
                }
            },
        });
    };

    useEffect(() => {
        if (businessScenario != null) {
            loadSchemeOptions({
                variables: {
                    businessScenario: businessScenario,
                    channelId: DSP_CLIENT_ID,
                    isBind: !isBindView
                }
            });
        } else {
            setSchemeOptions([]);
        }
    }, [businessScenario, isBindView, loadSchemeOptions]);

    useEffect(() => {
        if (mode === 'bind' || mode === 'bindEdit') {
            loadDspOptions();
        }
    }, [mode, loadDspOptions]);

    useEffect(() => {
        if (schemeData?.allOptions) {
            setSchemeOptions(schemeData.allOptions);
        }
        if (dspOptionsData?.allOptions) {
            setDspOptions(dspOptionsData.allOptions);
        }
    }, [schemeData, dspOptionsData, selectedSchemeId]);

    useEffect(() => {
        setSchemeDesc('');
        setFeeItemList([]);

        if (selectedSchemeId) {
            if (isBindView) {
                loadSchemeDetail({
                    variables: {
                        pricingSchemeId: selectedSchemeId || 0
                    }
                });
            } else {
                loadApplyDetail({
                    variables: {
                        clientId: DSP_CLIENT_ID,
                        pricingSchemeId: selectedSchemeId || 0
                    }
                });
                loadSchemeDetail({
                    variables: {
                        pricingSchemeId: selectedSchemeId || 0
                    }
                });
            }
        }
    }, [selectedSchemeId, isBindView, loadSchemeDetail, loadApplyDetail]);

    useEffect(() => {
        const detailResult = schemeDetailData?.getPricingSchemeDetail;
        if (detailResult?.messageKey || detailResult?.message) {
            message.error(resolvePricingSchemeMessage(detailResult, '加载定价方案详情失败'));
        }
        if (schemeDetailData?.getPricingSchemeDetail) {
            const detail = schemeDetailData.getPricingSchemeDetail;
            setSchemeDesc(detail?.pricingSchemeDescription || '');

            const newFeeItemList: Array<{ label: string; key: string; children: JSX.Element }> = [];
            detail.priceConfig.forEach((priceConfig) => {
                const feeItemCode = priceConfig.pricingFeeItemCode.toString();
                const feeItemTab = feeItemTabMap[feeItemCode];

                if (feeItemTab) {
                    const newPriceItems = priceConfig.allPriceConfig.map((config) => {
                        return {
                            groupName: config.primaryFactorConfigDesc,
                            priceItems: JSON.parse(config.contentPriceConfig)
                        };
                    });

                    let component = <DspDeliveryFeeViewTab renderData={newPriceItems} />;
                    
                    newFeeItemList.push({
                        label: feeItemTab.label,
                        key: feeItemCode,
                        children: component
                    });
                }
            });
            setFeeItemList(newFeeItemList);
        } else {
            setFeeItemList([]);
        }
    }, [schemeDetailData]);

    const timeZoneOptions = [
        { value: 'Asia/Shanghai', label: 'Asia/Shanghai' },
        { value: 'America/Los_Angeles', label: 'America/Los_Angeles' },
    ];

    useEffect(() => {
        const applyDetailResult = applyDetailData?.getClientPricingSchemeApplyDetail;
        if (applyDetailResult?.messageKey || applyDetailResult?.message) {
            message.error(resolvePricingSchemeMessage(applyDetailResult, '加载客户方案绑定详情失败'));
        }
    }, [applyDetailData]);

    const bindDsps = useMemo(() => {
        if (applyDetailData?.getClientPricingSchemeApplyDetail) {
            return applyDetailData.getClientPricingSchemeApplyDetail.bindValueDesc ||
                applyDetailData.getClientPricingSchemeApplyDetail.bindValues.join(', ');
        }
        return '';
    }, [applyDetailData]);

    const timezone = useMemo(() => {
        if (applyDetailData?.getClientPricingSchemeApplyDetail) {
            return applyDetailData.getClientPricingSchemeApplyDetail.timezone || '';
        }
        return '';
    }, [applyDetailData]);

    const bindEffectiveTime = useMemo(() => {
        const detail = applyDetailData?.getClientPricingSchemeApplyDetail;
        if (detail?.effectiveTime) {
            return formatApplyTimeWithTimezone(detail.effectiveTime, timezone);
        }
        return '';
    }, [applyDetailData, timezone]);

    const unbindOptions = useMemo(() => {
        if (applyDetailData?.getClientPricingSchemeApplyDetail) {
            const detail = applyDetailData.getClientPricingSchemeApplyDetail;
            const valueList = detail.bindValues || [];
            const labelList = (detail.bindValueDesc || '').split(',').map(item => item.trim());
            return valueList.map((value, index) => ({
                value: value,
                label: labelList[index] || value
            }));
        }
        return [];
    }, [applyDetailData]);

    const appendOptions = useMemo(() => {
        if (!applyDetailData?.getClientPricingSchemeApplyDetail) {
            return [];
        }
        if (dspOptions.length === 0) {
            return [];
        }
        const detail = applyDetailData.getClientPricingSchemeApplyDetail;
        const boundSet = new Set(detail.bindValues || []);
        return dspOptions.filter(option => !boundSet.has(option.value));
    }, [applyDetailData, dspOptions]);

    const [checkEffectType, setCheckEffectType] = useState(1);

    const onChange = (e: RadioChangeEvent) => {
        setCheckEffectType(e.target.value);
    };

    const dateFormat = 'YYYY/MM/DD';
    const plainOptions = [{
        value: 1,
        label: '立即生效',
    }, {
        value: 2,
        label: (
            <>
                <span style={{ marginRight: '10px' }}>未来生效</span>
                {checkEffectType === 2 && (
                    <span>
                        <DatePicker
                            value={effectiveDate}
                            onChange={handleDateChange}
                            format={dateFormat}
                            minDate={dayjs().add(1, 'day').startOf('day')}
                            placeholder="选择生效日期"
                        />
                    </span>
                )}
            </>
        ),
    }];

    const RadioStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
    };

    return (
        <div>
            <Row justify="start" gutter={[16, 8]}>
                <Col span={12}>
                    <ParcelFilterStyle
                        label="选择方案"
                        options={schemeOptions}
                        spanClassName="w-[9em]"
                        value={selectedSchemeId?.toString() || ''}
                        onChange={handleSchemeChange}
                        disabled={schemeLoading || !businessScenario}
                        placeholder={!businessScenario ? "请先选择业务场景" : "请选择方案"}
                    />
                </Col>
                <Col span={12}>
                    <p style={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>{schemeDesc}</p>
                </Col>
            </Row>
            <Row justify="center" gutter={[8, 16]}>
                <Col span={24} />
                <Col>
                    <PricingItemPanel
                        priceItemTabs={feeItemList}
                    />
                </Col>
            </Row>
            <Divider />

            {isBindView ? (
                <div>
                    <Row justify="center" align='middle' gutter={[8, 16]}>
                        <Col span={3}>
                            <span className="text-gray-500 mr-2 text-left break-words min-w-0">DSP客户维度</span>
                        </Col>
                        <Col span={18}>
                            <Select
                                mode="multiple"
                                size='large'
                                style={{ width: '100%' }}
                                value={selectedDspIds}
                                onChange={handleSelectedDspChange}
                                // 选中后保留搜索词，便于连续选择同一前缀下的多个维度
                                autoClearSearchValue={false}
                                options={dspOptions}
                                disabled={dspOptionsLoading || !selectedSchemeId}
                                placeholder={!selectedSchemeId ? "请先选择方案" : "请选择DSP客户维度（可搜索）"}
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase()) ||
                                    (option?.value ?? '').toString().toLowerCase().includes(input.toLowerCase())
                                }
                            />
                        </Col>
                    </Row>
                    <Divider />
                    <Row justify="center" align='middle' gutter={[8, 16]}>
                        <Col span={5}>
                            <span className="text-gray-500 mr-2 text-left break-words min-w-0">生效时间</span>
                        </Col>
                        <Col span={10}>
                            <Radio.Group
                                style={RadioStyle}
                                options={plainOptions}
                                value={checkEffectType}
                                onChange={onChange}
                                disabled={!selectedSchemeId}
                            />
                        </Col>
                        <Col span={6}>
                            <span style={{ marginLeft: '10px' }}>时区</span>
                            <Select
                                style={{ minWidth: 180, marginLeft: '10px' }}
                                options={timeZoneOptions}
                                value={timeZone}
                                onChange={handleTimeZoneChange}
                            />
                        </Col>
                    </Row>
                    <Row justify="end" style={{ margin: 25 }}>
                        <Col>
                            <Button type="default" size="large" style={{ margin: 15 }} disabled={bindLoading} onClick={handleClose}>返回</Button>
                            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PRICE_APPLY_OPERATE]}>
                                <Button type="primary" size="large" style={{ margin: 15 }} onClick={handleSave} loading={bindLoading}>保存</Button>
                            </HasResource>
                        </Col>
                    </Row>
                </div>
            ) : isUnbindView ? (
                <div>
                    <Row justify="center" align='middle' gutter={[8, 16]}>
                        <Col span={3}>
                            <span className="text-gray-500 mr-2 text-left break-words min-w-0">已绑定维度</span>
                        </Col>
                        <Col span={18}>
                            <span>{bindDsps}</span>
                        </Col>
                    </Row>
                    <Divider />
                    <Row justify="center" align='middle' gutter={[8, 16]}>
                        <Col span={3}>
                            <span className="text-gray-500 mr-2 text-left break-words min-w-0">生效时间</span>
                        </Col>
                        <Col span={18}>
                            <span>{bindEffectiveTime || '暂无数据'}</span>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <span>{timezone || ''}</span>
                        </Col>
                    </Row>
                    <Divider />
                    <Row justify="center" align='middle' gutter={[8, 16]}>
                        <Col span={3}>
                            <span className="text-gray-500 mr-2 text-left break-words min-w-0">解绑维度</span>
                        </Col>
                        <Col span={18}>
                            <Select
                                mode="multiple"
                                size='large'
                                style={{ width: '100%' }}
                                value={unbindDspIds}
                                onChange={handleUnbindDspChange}
                                // 选中后保留搜索词，便于连续选择同一前缀下的多个维度
                                autoClearSearchValue={false}
                                options={unbindOptions}
                                disabled={applyDetailLoading || !selectedSchemeId || unbindOptions.length === 0}
                                placeholder={!selectedSchemeId ? "请先选择方案" : unbindOptions.length === 0 ? "暂无可解绑的维度" : "请选择要解绑的DSP客户维度"}
                            />
                        </Col>
                    </Row>
                    <Row justify="end" style={{ margin: 25 }}>
                        <Col>
                            <Button type="default" size="large" style={{ margin: 15 }} disabled={unbindLoading} onClick={handleClose}>返回</Button>
                            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PRICE_APPLY_OPERATE]}>
                                <Button type="primary" size="large" style={{ margin: 15 }} onClick={handleUnbindSave} loading={unbindLoading}>确定解绑</Button>
                            </HasResource>
                        </Col>
                    </Row>
                </div>
            ) : (
                <div>
                    <Row justify="center" align='middle' gutter={[8, 16]}>
                        <Col span={3}>
                            <span className="text-gray-500 mr-2 text-left break-words min-w-0">已绑定维度</span>
                        </Col>
                        <Col span={18}>
                            <span>{bindDsps}</span>
                        </Col>
                    </Row>
                    <Divider />
                    <Row justify="center" align='middle' gutter={[8, 16]}>
                        <Col span={3}>
                            <span className="text-gray-500 mr-2 text-left break-words min-w-0">生效时间</span>
                        </Col>
                        <Col span={18}>
                            <span>{bindEffectiveTime || '暂无数据'}</span>
                            &nbsp;&nbsp;&nbsp;&nbsp;
                            <span>{timezone || ''}</span>
                        </Col>
                    </Row>
                    <Divider />
                    <Row justify="center" align='middle' gutter={[8, 16]}>
                        <Col span={3}>
                            <span className="text-gray-500 mr-2 text-left break-words min-w-0">追加绑定维度</span>
                        </Col>
                        <Col span={18}>
                            <Select
                                mode="multiple"
                                size='large'
                                style={{ width: '100%' }}
                                value={appendDspIds}
                                onChange={handleAppendDspChange}
                                // 选中后保留搜索词，便于连续选择同一前缀下的多个维度
                                autoClearSearchValue={false}
                                options={appendOptions}
                                disabled={dspOptionsLoading || applyDetailLoading || !selectedSchemeId || appendOptions.length === 0}
                                placeholder={
                                    !selectedSchemeId ? "请先选择方案" :
                                        dspOptionsLoading ? "加载中..." :
                                            appendOptions.length === 0 ? "暂无可追加的维度" :
                                            "请选择要追加绑定的DSP客户维度"
                                }
                            />
                        </Col>
                    </Row>
                    <Row justify="end" style={{ margin: 25 }}>
                        <Col>
                            <Button type="default" size="large" style={{ margin: 15 }} disabled={appendBindLoading} onClick={handleClose}>返回</Button>
                            <HasResource resourceCodes={[ResourceCode.settlement.SETTLEMENT_COST_PRICE_APPLY_OPERATE]}>
                                <Button
                                    type="primary"
                                    size="large"
                                    style={{ margin: 15 }}
                                    onClick={handleAppendSave}
                                    loading={appendBindLoading}
                                    disabled={appendOptions.length === 0 || appendDspIds.length === 0 || dspOptionsLoading}
                                >
                                    确定追加绑定
                                </Button>
                            </HasResource>
                        </Col>
                    </Row>
                </div>
            )}
        </div>
    );
};

export default DSPApplySchemeComp;
